
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";

import { Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import { AppContext } from "../../App";
import { controller, endpoints } from "../../api/actions/api";
import { safeOnComplete } from "../../utils/safeOnComplete";
import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "./ReportPrintButton";

const TopSelling = (props) => {
  const { t } = useTranslation();
  const { _store, _getById } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  // Original sales returned by API
  const [sales, setSales] = useState([]);

  // Period filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const invalidRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  useEffect(() => {
    setSales([]);

    if (!_store?.id) return;

    setLoader(true);

    const onComplete = (res, err) => {
      safeOnComplete(
        (response) => {
          setSales(
            Array.isArray(response.result)
              ? response.result
              : []
          );
        },
        (error) => {
          console.error("Failed to load sells:", error);
          setSales([]);
        }
      )(res, err);

      setLoader(false);
    };

    _getById(
      endpoints.Sell + controller.GetSellsByStore,
      _store.id,
      null,
      onComplete
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  // Filter sales by date and calculate Top Selling
  const reportRows = useMemo(() => {
    const groupedProducts = new Map();

    sales.forEach((sale) => {
      // Apply date filters
      if (fromDate || toDate) {
        if (!sale.sellDate) return;

        const saleDate = new Date(sale.sellDate);

        if (Number.isNaN(saleDate.getTime())) {
          return;
        }

        if (fromDate) {
          const from = new Date(
            `${fromDate}T00:00:00`
          );

          if (saleDate < from) return;
        }

        if (toDate) {
          const to = new Date(
            `${toDate}T23:59:59.999`
          );

          if (saleDate > to) return;
        }
      }

      // Group products from selected sales
      (sale.sellItems || []).forEach((item) => {
        if (item.itemType !== 1 && item.itemType !== 2) {
          return;
        }

        if (item.itemType === 1 && !item.product) {
          return;
        }

        // Preserve the original discount row
        const key =
          item.itemType === 2
            ? "discount"
            : `product-${item.product.id}`;

        if (!groupedProducts.has(key)) {
          groupedProducts.set(key, {
            name:
              item.itemType === 2
                ? t("discount")
                : item.product.name,

            category:
              item.itemType === 2
                ? ""
                : item.product.category?.name || "",

            itemSold: 0,
            totalSale: 0,
          });
        }

        const product = groupedProducts.get(key);

        const qty = Number(item.qty || 0);
        const price = Number(item.price || 0);

        product.itemSold += qty;
        product.totalSale += qty * price;
      });
    });

    // Sort highest quantity sold first
    return Array.from(groupedProducts.values())
      .sort((a, b) => b.itemSold - a.itemSold)
      .map((product) => ({
        name: product.name,
        category: product.category,
        itemSold: product.itemSold,
        totalSale: formatCurrency(
          product.totalSale,
          _store
        ),
      }));
  }, [sales, fromDate, toDate, _store, t]);

  // Shared columns for screen and print
  const columns = [
    {
      id: "name",
      label: t("productName"),
    },
    {
      id: "category",
      label: t("category"),
    },
    {
      id: "itemSold",
      label: t("itemSold"),
    },
    {
      id: "totalSale",
      label: t("totalSale"),
    },
  ];

  const tableData = invalidRange ? [] : reportRows;

  return (
    <>
      {/* Date filters and Print Preview button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ mr: 1 }}>
          {t("topSelling")}
        </Typography>

        <TextField
          label={t("fromDate")}
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) =>
            setFromDate(e.target.value)
          }
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          label={t("toDate")}
          type="date"
          size="small"
          value={toDate}
          onChange={(e) =>
            setToDate(e.target.value)
          }
          inputProps={{
            min: fromDate || undefined,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          error={invalidRange}
          helperText={
            invalidRange
              ? "To Date must be on or after From Date"
              : ""
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          {t("clear")}
        </Button>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("topSelling")}
        />
      </Box>

      {/* Normal screen report */}
      {/* Search and Pagination remain enabled */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("topSelling")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        <Table
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={columns}
          tblData={tableData}
        />
      </Box>

      {/* Separate printable report - all rows */}
      <Box
        ref={reportRef}
        className="top-selling-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("topSelling")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        {/* All records without Search or Pagination */}
        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={columns}
          tblData={tableData}
        />
      </Box>
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(TopSelling);
